import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Stack,
  CircularProgress,
  Avatar,
} from "@mui/material";
import callApi from "../../services/callApi";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { limpiarBaul, escribirBaul } from "../../services/referralBaul";

import HeaderProfesionales from "../landing-profesionales/components/HeaderProfesionales";
import medicalFunnelImg from "../../assets/images/referidos/medical-funnel.webp";

// ─── Constantes de UI ────────────────────────────────────────────────────────

const BeneficiosList = [
  { text: "Elimina el caos en tu agenda", icon: <EventAvailableIcon sx={{ fontSize: { xs: 28, md: 32 } }} /> },
  { text: "Cobra sin perseguir a tus pacientes", icon: <AttachMoneyIcon sx={{ fontSize: { xs: 28, md: 32 } }} /> },
  { text: "Emite las boletas con un click", icon: <ReceiptIcon sx={{ fontSize: { xs: 28, md: 32 } }} /> }
];

const CardMD = ({ text, icon }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      p: 2.5,
      borderRadius: "16px",
      backgroundColor: "#ffffff",
      border: "1px solid #eaeaea",
      height: "100%",
      minHeight: "100px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      textAlign: "center",
    }}
  >
    <Box sx={{ mb: 1.5, color: "#1877f2", display: "flex", alignItems: "center" }}>
      {icon}
    </Box>
    <Typography sx={{ fontFamily: "Poppins-SemiBold", color: "#1c1c1c", fontSize: "16px", lineHeight: 1.25 }}>
      {text}
    </Typography>
  </Paper>
);

const CardXS = ({ text, icon }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      p: 2,
      borderRadius: "16px",
      backgroundColor: "#ffffff",
      border: "1px solid #eaeaea",
      height: "100%",
      minHeight: "72px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      textAlign: "left",
    }}
  >
    <Box sx={{ mr: 2, color: "#1877f2", display: "flex", alignItems: "center" }}>
      {icon}
    </Box>
    <Typography sx={{ fontFamily: "Poppins-SemiBold", color: "#1c1c1c", fontSize: "14px", lineHeight: 1.25 }}>
      {text}
    </Typography>
  </Paper>
);

// Nota: la generación de textos de regalo se hace en src/services/referralBaul.jsx
// y se almacena en el baúl. Aquí sólo usamos el texto del baúl para el render.

export default function LandingReferidos() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [baul, setBaul] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function fetchReferral() {
      if (!slug) return;
      try {
        // ── Siempre limpiar el baúl anterior antes de escribir el nuevo ──────
        limpiarBaul();

        // ── Generar o recuperar el browser_id persistente en localStorage ────
        const BROWSER_ID_KEY = "clinifica_browser_id";
        let browserId = localStorage.getItem(BROWSER_ID_KEY);
        if (!browserId) {
          browserId = crypto.randomUUID();
          localStorage.setItem(BROWSER_ID_KEY, browserId);
        }

        const response = await callApi(
          `/agenda/referidosPerfil?link=${slug}&browser_id=${browserId}`,
          "get"
        );

        if (response && response.profile) {
          // ── Escritura atómica: un solo JSON, nunca estado parcial ─────────
          const baulEscrito = escribirBaul(response, slug);
          setBaul(baulEscrito);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching referral:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchReferral();
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isInvalid = error || !baul;

  // Datos del baúl para el render
  const foto       = baul?.foto       || null;
  const colorPastel = baul?.colorPastel || null;
  const primerNombre = baul?.mensajeInvitacion
    ? baul.mensajeInvitacion.split(" ")[0]
    : "Un profesional";

  const giftText = isInvalid
    ? "Te invitamos a probar Clinifica"
    : (baul?.mensajeInvitacion || "Te invitamos a probar Clinifica");

  // Alianza DuoEmprende (Medical Funnel): imagen rectangular y textos propios
  const esDuoEmprende = !isInvalid && baul?.esDuoEmprende === true;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#ffffff", pb: { xs: 12, md: 6 } }}>

      {/* Header Global */}
      <HeaderProfesionales />

      {/* Contenedor Principal */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", md: "center" },
          alignItems: "center",
          boxSizing: "border-box",
          width: "100%",
          px: 2,
          pt: { xs: "100px", md: "120px" },
          pb: 4
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>

          {/* Bloque 1: Foto + Título */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: { xs: 4, md: 5 } }}
          >
            {/* Foto de Perfil (o imagen rectangular de la alianza DuoEmprende) */}
            {!isInvalid && (
              esDuoEmprende ? (
                <Box
                  component="img"
                  src={medicalFunnelImg}
                  alt="Medical Funnel"
                  sx={{
                    width: { xs: "100%", md: "300px" },
                    maxWidth: { xs: "340px", md: "300px" },
                    height: "auto",
                    borderRadius: "16px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                    flexShrink: 0,
                    display: "block"
                  }}
                />
              ) : foto ? (
                <Avatar
                  src={foto}
                  alt={primerNombre}
                  variant="rounded"
                  sx={{
                    width: { xs: 120, md: 150 },
                    height: { xs: 120, md: 150 },
                    borderRadius: "16px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.1)"
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: { xs: 120, md: 150 },
                    height: { xs: 120, md: 150 },
                    borderRadius: "16px",
                    backgroundColor: colorPastel || "#BAE1FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.1)"
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Poppins-SemiBold",
                      fontSize: { xs: "56px", md: "72px" },
                      color: "#1c1c1c",
                      lineHeight: 1
                    }}
                  >
                    {primerNombre ? primerNombre[0].toUpperCase() : "?"}
                  </Typography>
                </Box>
              )
            )}
            <Typography
              variant="h1"
              sx={{
                fontFamily: esDuoEmprende ? "Poppins-Regular" : "Poppins-SemiBold",
                fontSize: { xs: "24px", md: "38px" },
                color: "#1c1c1c",
                textAlign: { xs: "center", md: "left" },
                flex: 1
              }}
            >
              {isInvalid
                ? "Te invitamos a sumar más tiempo para ti"
                : esDuoEmprende
                  ? (
                    <>
                      DuoEmprende y su programa{" "}
                      <Box component="span" sx={{ fontFamily: "Poppins-Bold" }}>Medical Funnel</Box>{" "}
                      te apoya en el crecimiento de tu consulta ✨
                    </>
                  )
                  : `${primerNombre} cree en tu talento y quiere ayudarte a que tu consulta crezca al siguiente nivel ✨`}
            </Typography>
          </Stack>

          {/* Bloque 2: Mensaje de invitación */}
          <Typography
            sx={{
              fontFamily: "Quicksand-Medium",
              fontSize: { xs: "16px", md: "18px" },
              color: "#5f6368",
              mb: 1.5,
              lineHeight: 1.5,
              maxWidth: "800px",
              mx: "auto"
            }}
          >
            Comienza a usar Clinifica y automatiza por completo tu trabajo como independiente
          </Typography>

          {/* El Regalo reubicado */}
          <Typography
            sx={{
              fontFamily: esDuoEmprende ? "Poppins-Regular" : "Poppins-SemiBold",
              fontSize: { xs: "18px", md: "22px" },
              color: "#1877f2",
              mb: { xs: 4, md: 5 },
            }}
          >
            {esDuoEmprende
              ? (
                <>
                  Por ser parte de Medical Funnel, accede a un descuento exclusivo del{" "}
                  <Box component="span" sx={{ fontFamily: "Poppins-Bold" }}>{baul.beneficioDuo}</Box>{" "}
                  en el Plan Pro de Clinifica.
                </>
              )
              : giftText}
          </Typography>

          {/* Bloque 3: Las 3 Cajas */}
          {/* Versión Desktop (MD) */}
          <Stack
            direction="row"
            spacing={3}
            justifyContent="center"
            alignItems="stretch"
            sx={{ display: { xs: "none", md: "flex" }, width: "100%" }}
          >
            {BeneficiosList.map((item, idx) => (
              <CardMD key={idx} text={item.text} icon={item.icon} />
            ))}
          </Stack>

          {/* Versión Mobile (XS) */}
          <Stack
            direction="column"
            spacing={2}
            justifyContent="center"
            alignItems="stretch"
            sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}
          >
            {BeneficiosList.map((item, idx) => (
              <CardXS key={idx} text={item.text} icon={item.icon} />
            ))}
          </Stack>

          {/* Bloque 4: Boton MD e Info */}
          <Box sx={{ mt: { xs: 4, md: 5 }, textAlign: "center", mb: { xs: 2, md: 6 } }}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                color: "#5f6368",
                mb: { xs: 4, md: 5 }
              }}
            >
              Registrarte es gratis y puedes activar el regalo cuando quieras
            </Typography>

            {/* Botón clásico solo visible en Desktop (MD) */}
            <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
              <Container maxWidth="xs" disableGutters>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => navigate("/pro/registro")}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#1877f2",
                    fontFamily: "Quicksand, sans-serif",
                    "&:hover": {
                      backgroundColor: "#166fe5",
                    },
                    boxShadow: "0px 6px 16px rgba(24, 119, 242, 0.3)",
                  }}
                >
                  Regístrate gratis
                </Button>
              </Container>
            </Box>
          </Box>

        </Container>
      </Box>

      {/* Bloque Fixed exclusivo para Mobile (XS) */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#ffffff",
          borderTop: "1px solid #eaeaea",
          px: 2,
          py: 2,
          zIndex: 1000,
          justifyContent: "center",
          boxSizing: "border-box"
        }}
      >
        <Container maxWidth="xs" disableGutters>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate("/pro/registro")}
            sx={{
              py: 1.5,
              fontSize: { md: "16px", xs: "14px" },
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#1877f2",
              fontFamily: "Quicksand-Bold",
              "&:hover": {
                backgroundColor: "#166fe5",
              },
              boxShadow: "none",
            }}
          >
            Regístrate gratis
          </Button>
        </Container>
      </Box>

    </Box>
  );
}
